import { NotebookDialog } from "@/modules/note-taker/notebook-dialog";
import { TasksTrigger } from "@/modules/note-taker/tasks-trigger";
import { useFamilyMemberEvents } from "@/modules/note-taker/use-family-member-events";
import { handleError } from "@/utils/error";
import { useUpdateEvent } from "creso-core/hooks";
import { FamilyDetail } from "creso-core/types";
import { format } from "date-fns";
import { PlusIcon } from "lucide-react";
import { useMemo } from "react";
import { Button, Checkbox, cn, V2Label } from "web-design-system";

export function FamilyTasks(props: { family: FamilyDetail }) {
  const today = format(new Date(), "yyyy-MM-dd");
  const members = useMemo(
    () => props.family.members ?? [],
    [props.family.members]
  );
  const memberIds = useMemo(
    () => members.map((member) => member.client_id),
    [members]
  );

  const { data: events } = useFamilyMemberEvents(memberIds, today);

  const { mutateAsync: updateEvent, isPending } = useUpdateEvent();

  const tasks = useMemo(
    () =>
      events
        .filter((event) => event.type === "NOTE" || event.type === "TASK")
        .sort((a, b) => a.id - b.id),
    [events]
  );

  if (!members.length) return null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row gap-2 items-center">
        <NotebookDialog
          trigger={(triggerProps) => (
            <TasksTrigger {...triggerProps} label="Today's tasks" />
          )}
          view="calendar"
          clientIds={memberIds}
        />
        <NotebookDialog
          trigger={({ onClick }) => (
            <Button
              onClick={onClick}
              size="icon"
              className="ml-auto"
              variant="outline"
            >
              <PlusIcon />
            </Button>
          )}
          action="add-task"
          view="calendar"
          clientIds={memberIds}
        />
      </div>

      <div className="px-2 flex flex-col gap-4">
        {tasks.map((task) => (
          <div key={task.id} className="flex flex-row gap-3 items-start">
            <Checkbox
              checked={task.is_deprecated}
              disabled={isPending}
              onCheckedChange={(checked) =>
                updateEvent({
                  ...task,
                  is_deprecated: !!checked,
                }).catch((error) =>
                  handleError(error, "Failed to update task.")
                )
              }
              className="mt-px"
              id={task.id.toString()}
            />
            <V2Label
              className={cn(
                "font-normal leading-5",
                task.is_deprecated && "line-through text-muted-foreground"
              )}
              htmlFor={task.id.toString()}
            >
              <span>{task.note}</span>
              {members.length > 1 && task.client_name ? (
                <span className="block text-xs text-muted-foreground">
                  {task.client_name}
                </span>
              ) : null}
            </V2Label>
          </div>
        ))}
      </div>
    </div>
  );
}
